/**
 * @fileoverview ClubOS Revenue Management page — FY 2025-26
 * Revenue entries with TDS tracking per Indian tax rules.
 * TODO: Replace mock data with API calls: financeService.getRevenue(), financeService.addRevenueEntry()
 */

import { useState, useMemo, useEffect, useCallback } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import SoftProgress from "components/SoftProgress";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// ClubOS utilities & data
import { formatCompact, formatDateIN, CLUB_NAME } from "utils/indiaLocale";
import { PermissionGate } from "components/PermissionGate";
import { financeService } from "api";
import FootballLoader from "components/FootballLoader";
import { useAuth } from "context/AuthContext";

// ── API → UI shape mapper ────────────────────────────────────────────────────
function mapApiRecord(dto) {
  const gross = dto.amount;
  const tdsRate = 0;  // TDS not in DTO; kept as 0 for now
  return {
    id:          dto.id,
    date:        dto.transactionDate,
    category:    dto.category || "Other",
    source:      dto.description,
    grossAmount: gross,
    tdsRate,
    tdsAmount:   0,
    netAmount:   gross,
    invoiceRef:  dto.reference || "",
    notes:       dto.notes || "",
    status:      "Received",
  };
}

// ─── CONSTANTS ─────────────────────────────────────────────────────────────────

/** Revenue categories with default TDS rates per Indian tax rules */
const REVENUE_CATEGORIES = [
  { label: "Broadcasting",  defaultTdsRate: 10 },
  { label: "Sponsorship",   defaultTdsRate: 5  },
  { label: "Matchday",      defaultTdsRate: 0  },
  { label: "Prize Money",   defaultTdsRate: 10 },
  { label: "Transfer",      defaultTdsRate: 10 },
  { label: "Academy",       defaultTdsRate: 0  },
  { label: "Other",         defaultTdsRate: 5  },
];

const CATEGORY_NAMES = REVENUE_CATEGORIES.map((c) => c.label);

/** Status badge colours */
const STATUS_COLORS = {
  Received: "success",
  Pending:  "warning",
  Overdue:  "error",
};

/** Empty revenue entry form */
const EMPTY_FORM = {
  date: "",
  category: "",
  source: "",
  grossAmount: "",
  tdsRate: "",
  invoiceRef: "",
  notes: "",
  status: "Received",
};

// ─── COMPONENT ─────────────────────────────────────────────────────────────────

function RevenuePage() {
  const { currentUser } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEntries = useCallback(() => {
    setLoading(true);
    financeService.getRecords({ category: "Income" })
      .then((data) => setEntries(data.map(mapApiRecord)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Filters
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  // ── Filtered entries ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return entries.filter((e) => {
      const matchSearch = e.source.toLowerCase().includes(search.toLowerCase());
      const matchCat = categoryFilter === "All" || e.category === categoryFilter;
      const matchStatus = statusFilter === "All" || e.status === statusFilter;
      return matchSearch && matchCat && matchStatus;
    });
  }, [entries, search, categoryFilter, statusFilter]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const totalYTD       = entries.filter((e) => e.status === "Received").reduce((s, e) => s + e.netAmount, 0);
  const broadcasting   = entries.filter((e) => e.category === "Broadcasting" && e.status === "Received").reduce((s, e) => s + e.netAmount, 0);
  const commercial     = entries.filter((e) => e.category === "Sponsorship"  && e.status === "Received").reduce((s, e) => s + e.netAmount, 0);
  const otherRevenue   = entries.filter((e) => !["Broadcasting", "Sponsorship"].includes(e.category) && e.status === "Received").reduce((s, e) => s + e.netAmount, 0);
  const pendingCount   = entries.filter((e) => e.status === "Pending").length;
  const pendingAmount  = entries.filter((e) => e.status === "Pending").reduce((s, e) => s + e.grossAmount, 0);
  const totalTdsCollected = entries.filter((e) => e.status === "Received").reduce((s, e) => s + (e.tdsAmount || 0), 0);

  // ── Revenue by category (for progress bars) ───────────────────────────────
  const categoryTotals = useMemo(() => {
    const map = {};
    CATEGORY_NAMES.forEach((cat) => {
      map[cat] = entries.filter((e) => e.category === cat && e.status === "Received").reduce((s, e) => s + e.netAmount, 0);
    });
    return map;
  }, [entries]);

  // ── Dialog handlers ───────────────────────────────────────────────────────
  const openAddDialog = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    const updated = { ...form, [field]: value };

    // Auto-fill TDS rate when category changes
    if (field === "category") {
      const catConfig = REVENUE_CATEGORIES.find((c) => c.label === value);
      if (catConfig) updated.tdsRate = String(catConfig.defaultTdsRate);
    }

    setForm(updated);
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  /** Auto-calculated TDS and Net amounts */
  const computedTds = form.grossAmount
    ? Math.round(Number(form.grossAmount) * (Number(form.tdsRate) / 100))
    : 0;
  const computedNet = form.grossAmount ? Number(form.grossAmount) - computedTds : 0;

  const validateEntryForm = () => {
    const errors = {};
    if (!form.date) errors.date = "Date is required";
    if (!form.category) errors.category = "Category is required";
    if (!form.source.trim()) errors.source = "Source is required";
    if (!form.grossAmount || isNaN(Number(form.grossAmount))) errors.grossAmount = "Valid amount required";
    return errors;
  };

  const handleSave = async () => {
    const errors = validateEntryForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setLoading(true);
    try {
      await financeService.createRecord({
        description:     form.source,
        amount:          Number(form.grossAmount),
        currency:        "INR",
        category:        "Income",
        costCentre:      form.category,
        department:      form.category,
        transactionDate: form.date,
        reference:       form.invoiceRef || null,
        notes:           form.notes || null,
        tenantId:        currentUser.tenantId,
      });
      await fetchEntries();
      setDialogOpen(false);
    } catch (err) {
      setFormErrors({ submit: err?.title ?? err?.message ?? "Failed to add revenue entry" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* ── Page header ─────────────────────────────────────────────────── */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">
              Revenue Management
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              FY 2025-26 &middot; {CLUB_NAME} &middot; TDS tracking enabled
            </SoftTypography>
          </SoftBox>
          <PermissionGate permission="finance.revenue.edit">
            <SoftButton variant="gradient" color="info" onClick={openAddDialog}>
              <Icon sx={{ mr: 0.5 }}>add</Icon>
              Add Revenue Entry
            </SoftButton>
          </PermissionGate>
        </SoftBox>

        {/* ── Pending receivables alert ────────────────────────────────────── */}
        {pendingCount > 0 && (
          <SoftBox mb={3}>
            <Alert
              severity="warning"
              icon={<Icon>warning_amber</Icon>}
              sx={{ borderRadius: 2 }}
            >
              <strong>{pendingCount} pending receivable{pendingCount > 1 ? "s" : ""}</strong> totalling{" "}
              {formatCompact(pendingAmount)} — follow up required to ensure timely collection.
            </Alert>
          </SoftBox>
        )}

        {/* ── KPI Cards ───────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Revenue YTD" }}
                count={formatCompact(totalYTD)}
                percentage={{ color: "success", text: "net of TDS" }}
                icon={{ color: "success", component: "trending_up" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Broadcasting" }}
                count={formatCompact(broadcasting)}
                percentage={{ color: "info", text: "Star Sports + JioCinema" }}
                icon={{ color: "info", component: "live_tv" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Commercial" }}
                count={formatCompact(commercial)}
                percentage={{ color: "primary", text: "sponsorship income" }}
                icon={{ color: "primary", component: "handshake" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Other Revenue" }}
                count={formatCompact(otherRevenue)}
                percentage={{ color: "warning", text: "matchday + prizes + transfers" }}
                icon={{ color: "warning", component: "category" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Revenue by Category + TDS Summary ────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            {/* Revenue by Category */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                    Revenue by Category (YTD Received)
                  </SoftTypography>
                  {CATEGORY_NAMES.map((cat) => {
                    const val = categoryTotals[cat] || 0;
                    const pct = totalYTD > 0 ? Math.round((val / totalYTD) * 100) : 0;
                    if (val === 0) return null;
                    return (
                      <SoftBox key={cat} mb={1.5}>
                        <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                          <SoftTypography variant="caption" fontWeight="medium">{cat}</SoftTypography>
                          <SoftTypography variant="caption" color="text">
                            {formatCompact(val)} ({pct}%)
                          </SoftTypography>
                        </SoftBox>
                        <SoftProgress value={pct} color="info" />
                      </SoftBox>
                    );
                  })}
                </SoftBox>
              </Card>
            </Grid>

            {/* TDS Summary */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                    TDS &amp; Tax Summary
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mb={2}>
                    Tax Deducted at Source per Indian Income Tax rules.
                  </SoftTypography>

                  <SoftBox
                    p={2}
                    mb={2}
                    sx={{ backgroundColor: "#f8f9fa", borderRadius: 2, border: "1px solid #e9ecef" }}
                  >
                    <SoftBox display="flex" justifyContent="space-between" mb={1}>
                      <SoftTypography variant="caption" color="text">Total Gross Revenue YTD</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold">
                        {formatCompact(entries.filter((e) => e.status === "Received").reduce((s, e) => s + e.grossAmount, 0))}
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" justifyContent="space-between" mb={1}>
                      <SoftTypography variant="caption" color="text">Total TDS Collected</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" color="warning">
                        -{formatCompact(totalTdsCollected)}
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" justifyContent="space-between">
                      <SoftTypography variant="caption" fontWeight="bold">Net Revenue Received</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" color="success">
                        {formatCompact(totalYTD)}
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>

                  <SoftTypography variant="caption" color="text" display="block" mb={0.5}>
                    TDS Rate Summary by Category:
                  </SoftTypography>
                  {REVENUE_CATEGORIES.filter((c) => c.defaultTdsRate > 0).map((cat) => (
                    <SoftBox key={cat.label} display="flex" justifyContent="space-between" mb={0.25}>
                      <SoftTypography variant="caption" color="text">{cat.label}</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="medium">{cat.defaultTdsRate}% TDS</SoftTypography>
                    </SoftBox>
                  ))}
                  <SoftBox display="flex" justifyContent="space-between" mb={0.25}>
                    <SoftTypography variant="caption" color="text">Matchday / Academy</SoftTypography>
                    <SoftTypography variant="caption" fontWeight="medium">Nil TDS</SoftTypography>
                  </SoftBox>
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Revenue Entries Table ────────────────────────────────────────── */}
        <Card>
          <SoftBox p={3}>
            {/* Filters */}
            <Grid container spacing={2} mb={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  placeholder="Search source / payer..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <Icon sx={{ mr: 1, color: "#aaa", fontSize: "1.1rem" }}>search</Icon>,
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
                    <MenuItem value="All">All Categories</MenuItem>
                    {CATEGORY_NAMES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Received">Received</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Overdue">Overdue</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <SoftTypography variant="caption" color="text">
                  {filtered.length} of {entries.length} entries
                </SoftTypography>
              </Grid>
            </Grid>

            {/* Table */}
            <SoftBox sx={{ overflowX: "auto" }}>
              <SoftBox
                component="table"
                sx={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}
              >
                <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                  <SoftBox component="tr">
                    {["Date", "Category", "Source / Payer", "Gross Amount", "TDS", "Net Amount", "Invoice Ref", "Status"].map((h) => (
                      <SoftBox
                        key={h}
                        component="th"
                        sx={{
                          textAlign: "left",
                          p: 1.5,
                          color: "text.secondary",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          textTransform: "uppercase",
                          borderBottom: "2px solid #e9ecef",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
                <SoftBox component="tbody">
                  {loading ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={8}><FootballLoader label="Loading revenue entries…" /></SoftBox>
                    </SoftBox>
                  ) : filtered.length === 0 ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={8} sx={{ textAlign: "center", p: 4 }}>
                        <Icon sx={{ fontSize: "2rem", color: "#ccc", display: "block", mx: "auto", mb: 1 }}>
                          search_off
                        </Icon>
                        <SoftTypography variant="caption" color="text">
                          No revenue entries found matching your filters.
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  ) : (
                    filtered.map((entry, i) => (
                      <SoftBox
                        key={entry.id}
                        component="tr"
                        sx={{
                          "&:hover": { backgroundColor: "#f8f9fa" },
                          backgroundColor: i % 2 === 0 ? "white" : "#fdfdfd",
                        }}
                      >
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                          <SoftTypography variant="caption" color="text">
                            {formatDateIN(entry.date)}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftBadge badgeContent={entry.category} color="info" size="xs" container />
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", maxWidth: 200 }}>
                          <SoftTypography variant="caption" color="text" sx={{ fontSize: "0.72rem" }}>
                            {entry.source}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="button" fontWeight="medium">
                            {formatCompact(entry.grossAmount)}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          {entry.tdsAmount > 0 ? (
                            <Tooltip title={`${entry.tdsRate}% TDS = ${formatCompact(entry.tdsAmount)}`}>
                              <SoftTypography variant="caption" color="warning" fontWeight="medium">
                                -{formatCompact(entry.tdsAmount)}
                              </SoftTypography>
                            </Tooltip>
                          ) : (
                            <SoftTypography variant="caption" color="text">Nil</SoftTypography>
                          )}
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="button" fontWeight="bold" color="dark">
                            {formatCompact(entry.netAmount)}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace", fontSize: "0.68rem" }}>
                            {entry.invoiceRef}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                          <SoftBadge
                            badgeContent={entry.status}
                            color={STATUS_COLORS[entry.status] || "info"}
                            size="xs"
                            container
                          />
                        </SoftBox>
                      </SoftBox>
                    ))
                  )}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>

      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />

      {/* ── Add Revenue Entry Dialog ─────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>Add Revenue Entry</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} pt={1}>
            {/* Date */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Date"
                required
                type="date"
                value={form.date}
                onChange={(e) => handleFormChange("date", e.target.value)}
                error={!!formErrors.date}
                helperText={formErrors.date}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small" required error={!!formErrors.category}>
                <InputLabel shrink>Category</InputLabel>
                <Select
                  value={form.category}
                  label="Category"
                  onChange={(e) => handleFormChange("category", e.target.value)}
                >
                  {CATEGORY_NAMES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={(e) => handleFormChange("status", e.target.value)}>
                  <MenuItem value="Received">Received</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Invoice Ref */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Invoice / Ref #"
                value={form.invoiceRef}
                onChange={(e) => handleFormChange("invoiceRef", e.target.value)}
                size="small"
                placeholder="e.g. SSP-2026-0401"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Source */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Source / Payer"
                required
                value={form.source}
                onChange={(e) => handleFormChange("source", e.target.value)}
                error={!!formErrors.source}
                helperText={formErrors.source}
                size="small"
                placeholder="e.g. Star Sports — ISL TV Deal Q4"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Gross Amount */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Gross Amount (₹)"
                required
                type="number"
                value={form.grossAmount}
                onChange={(e) => handleFormChange("grossAmount", e.target.value)}
                error={!!formErrors.grossAmount}
                helperText={formErrors.grossAmount || (form.grossAmount ? formatCompact(Number(form.grossAmount)) : "")}
                size="small"
                inputProps={{ min: 0 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* TDS Rate */}
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="TDS Rate %"
                type="number"
                value={form.tdsRate}
                onChange={(e) => handleFormChange("tdsRate", e.target.value)}
                size="small"
                inputProps={{ min: 0, max: 30 }}
                helperText="Auto-filled by category"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* TDS Amount (read-only auto-calc) */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="TDS Amount (auto)"
                value={computedTds ? formatCompact(computedTds) : "₹0"}
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-root": { backgroundColor: "#f8f9fa" } }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Net Amount (read-only auto-calc) */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Net Amount (auto)"
                value={computedNet ? formatCompact(computedNet) : "₹0"}
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-root": { backgroundColor: "#f8f9fa" } }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={form.notes}
                onChange={(e) => handleFormChange("notes", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Upload Document — placeholder */}
            <Grid item xs={12}>
              <SoftButton variant="outlined" color="secondary" size="small" disabled>
                <Icon sx={{ mr: 0.5 }}>upload_file</Icon>
                Upload Document (Coming Soon)
              </SoftButton>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDialogOpen(false)}>
            Cancel
          </SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave}>
            Add Entry
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default RevenuePage;
